import React, { useState, useEffect } from 'react';
import { database, ref, get, set, update, push, child } from '../../firebase';

const FundAddByAdmin = () => {
  // Form states
  const [amount, setAmount] = useState('');
  const [wallet, setWallet] = useState('');
  const [operation, setOperation] = useState('add');
  const [type, setType] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState('form');

  // Data states
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // User search states
  const [userSearchInput, setUserSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogType, setDialogType] = useState('success');
  const [transactionDetails, setTransactionDetails] = useState(null);

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Show dialog function
  const showDialogMessage = (title, message, type = 'success', details = null) => {
    setDialogTitle(title);
    setDialogMessage(message);
    setDialogType(type);
    setTransactionDetails(details);
    setShowDialog(true);
  };

  // Close dialog
  const closeDialog = () => {
    setShowDialog(false);
    setDialogTitle('');
    setDialogMessage('');
    setDialogType('success');
    setTransactionDetails(null);
  };

  // Toggle accordion
  const toggleAccordion = (accordionId) => {
    setActiveAccordion(activeAccordion === accordionId ? null : accordionId);
  };

  // Fetch users data using Firebase SDK
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        const usersArray = [];

        for (const date in data) {
          const usersByDate = data[date];
          for (const userId in usersByDate) {
            const userData = usersByDate[userId];
            if (userData && userData.role === 'user') {
              const fundData = userData.fund || {};
              const sponsorId = userData.sponsorId || userData.userId || userId;
              
              usersArray.push({
                userId: userId,
                userDbKey: userId,
                name: userData.name || 'Unknown',
                fund: {
                  balance: fundData.balance || 0,
                  count: fundData.count || 0,
                  lastAmount: fundData.lastAmount || 0,
                  lastDate: fundData.lastDate || null,
                  lastTransactionId: fundData.lastTransactionId || '',
                  lastTransactionType: fundData.lastTransactionType || '',
                  lastTransactionWallet: fundData.lastTransactionWallet || '',
                  total: fundData.total || 0
                },
                status: userData.status || 'inactive',
                email: userData.email || '',
                mobile: userData.mobile || '',
                country: userData.country || '',
                registrationDate: userData.registrationDate || date,
                sponsorId: sponsorId,
                registrationDateFolder: date,
                wallets: userData.wallets || {}
              });
            }
          }
        }
        usersArray.sort((a, b) => a.name.localeCompare(b.name));
        setUsers(usersArray);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      showDialogMessage('Error', 'Failed to fetch users: ' + error.message, 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Get selected user
  const selectedUser = users.find(user => user.userId === selectedUserId);

  // Handle user search
  useEffect(() => {
    if (userSearchInput.trim() === '') {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const searchTerm = userSearchInput.toLowerCase().trim();
    const filtered = users.filter(user => {
      return (
        user.userId.toLowerCase().includes(searchTerm) ||
        user.name.toLowerCase().includes(searchTerm) ||
        user.email.toLowerCase().includes(searchTerm) ||
        user.sponsorId.toLowerCase().includes(searchTerm) ||
        user.mobile.includes(searchTerm)
      );
    }).slice(0, 5);
    setSuggestions(filtered);
    setShowSuggestions(true);
  }, [userSearchInput, users]);

  // Handle user selection
  const handleUserSelect = (user) => {
    setSelectedUserId(user.userId);
    setUserSearchInput(`${user.name} (${user.userId}) - Sponsor: ${user.sponsorId}`);
    setShowSuggestions(false);
  };

  // Clear user selection
  const handleClearUser = () => {
    setSelectedUserId('');
    setUserSearchInput('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Generate unique transaction ID
  const generateTransactionId = () => {
    return 'TXN' + Date.now() + Math.random().toString(36).substr(2, 9);
  };

  const getTransactionTypeLabel = (walletValue, operationValue) => {
    if (!walletValue) return '';
    const walletLabel =
      walletValue === 'Fund Wallet'
        ? 'AV Wallet'
        : walletValue === 'Main Wallet'
        ? 'AV Power'
        : 'AV Pay Wallet';
    return operationValue === 'return' ? `Debit ${walletLabel}` : `Credit ${walletLabel}`;
  };

  const getWalletDisplayName = (walletValue) => {
    if (walletValue === 'Fund Wallet') return 'AV Wallet';
    if (walletValue === 'Main Wallet') return 'AV Power Wallet';
    return 'AV Pay Wallet';
  };

  // Save to Admin Added Fund History
  const saveToAdminHistory = async (transactionData) => {
    try {
      // Get current date in DD-MM-YYYY format
      const today = new Date();
      const dateStr = `${today.getDate().toString().padStart(2, '0')}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getFullYear()}`;
      
      // Reference to adminaddedhistory with date and sponsorId structure
      const historyRef = ref(database, `adminaddedhistory/${dateStr}/${transactionData.sponsorId}`);
      
      // Get existing history for this user on this date
      const snapshot = await get(historyRef);
      let existingHistory = [];
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        existingHistory = Array.isArray(data) ? data : [data];
      }
      
      // Add new transaction to history
      existingHistory.push(transactionData);
      
      // Save back to Firebase
      await set(historyRef, existingHistory);
      
      console.log('Admin history saved successfully');
    } catch (error) {
      console.error('Error saving to admin history:', error);
      // Don't throw error - we don't want to block the main transaction
    }
  };

  // Save to UserFundTransactions
  const saveToUserFundTransactions = async (transactionData, userToUpdate, currentDateISO) => {
    try {
      const dateKey = currentDateISO.split('T')[0];
      const sponsorNode = transactionData.sponsorId || userToUpdate.sponsorId || userToUpdate.userId;
      const transactionRef = ref(database, `UserFundTransactions/${dateKey}/${sponsorNode}/${transactionData.transactionId}`);

      const entry = {
        transactionId: transactionData.transactionId,
        userId: transactionData.userId,
        memberId: transactionData.userId,
        userName: transactionData.userName,
        sponsorId: transactionData.sponsorId,
        amount: transactionData.amount,
        userAddedFund: transactionData.amount,
        wallet: transactionData.walletType,
        type: 'admin',
        description: transactionData.description,
        timestamp: currentDateISO,
        timestampFormatted: currentDateISO,
        source: 'admin',
        paymentMethod: 'admin',
        paymentDisplayMethod: transactionData.walletType,
        paymentId: transactionData.transactionId,
        currency: 'USD',
        displayCurrency: 'USD',
        status: 'completed',
        network: transactionData.walletType,
        fundBalance: transactionData.newBalance,
        previousFundBalance: transactionData.previousBalance,
        lastFundAmount: transactionData.amount,
        lastFundDate: currentDateISO
      };

      await set(transactionRef, entry);
    } catch (error) {
      console.error('Error saving to UserFundTransactions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUserId || !amount || !wallet || !type) {
      showDialogMessage('Missing Information', 'Please fill all required fields', 'error');
      return;
    }
    if (isNaN(amount) || Number(amount) <= 0) {
      showDialogMessage('Invalid Amount', 'Please enter a valid positive amount', 'error');
      return;
    }
    if (operation === 'return' && !description.trim()) {
      showDialogMessage('Reason Required', 'Please enter a reason for debit transaction', 'error');
      return;
    }
    
    setLoading(true);
    try {
      const userToUpdate = users.find(user => user.userId === selectedUserId);
      if (!userToUpdate) {
        showDialogMessage('User Not Found', 'Selected user not found in database', 'error');
        setLoading(false);
        return;
      }

      const registrationDateFolder = userToUpdate.registrationDateFolder;
      const userDbKey = userToUpdate.userId;
      const sponsorId = userToUpdate.sponsorId;

      // Get current user data using Firebase SDK
      const userRef = ref(database, `users/${registrationDateFolder}/${userDbKey}`);
      const userSnapshot = await get(userRef);
      
      if (!userSnapshot.exists()) {
        throw new Error('Failed to fetch user data');
      }
      
      const userData = userSnapshot.val();
      
      const amountNum = Number(amount);
      const transactionId = generateTransactionId();
      const currentDate = new Date().toISOString();

      // Prepare common transaction data
      const transactionData = {
        transactionId,
        userId: selectedUserId,
        userName: userToUpdate.name,
        sponsorId: sponsorId,
        amount: operation === 'return' ? -amountNum : amountNum,
        walletType: getWalletDisplayName(wallet),
        transactionType: type,
        description: description || (operation === 'return' ? 'Admin debited from user wallet' : 'Admin credited funds'),
        date: currentDate,
        adminName: 'Admin',
        adminId: 'admin',
        previousBalance: wallet === 'Fund Wallet' 
          ? (userData.wallets?.aurum || 0) 
          : wallet === 'Main Wallet'
          ? (userData.wallets?.working || 0)
          : (userData.fund?.balance || 0),
        newBalance: 0 // Will be set after update
      };

      // Based on wallet selection
      if (wallet === 'Fund Wallet') {
        // AV Wallet - Save to wallets.aurum
        const currentAurumBalance = Number(userData.wallets?.aurum || 0);
        const newAurumBalance = operation === 'return'
          ? currentAurumBalance - amountNum
          : currentAurumBalance + amountNum;
        if (newAurumBalance < 0) {
          throw new Error(`Insufficient AV Wallet balance. Current: $${currentAurumBalance.toFixed(2)}`);
        }
        transactionData.newBalance = newAurumBalance;

        // Update wallets.aurum
        const walletUpdateData = {
          wallets: {
            ...userData.wallets,
            aurum: newAurumBalance
          }
        };

        await update(userRef, walletUpdateData);

        const successDetails = {
          memberName: userToUpdate.name,
          amount: amountNum,
          transactionType: operation === 'return' ? 'Debit AV Wallet' : 'Credit AV Wallet',
          newBalance: newAurumBalance,
          walletType: 'AV Wallet',
          sponsorId: sponsorId,
          transactionId: transactionId
        };

        // Save histories
        await saveToAdminHistory(transactionData);
        await saveToUserFundTransactions(transactionData, userToUpdate, currentDate);

        showDialogMessage(
          '✅ Success', 
          operation === 'return' ? `AV Wallet debit processed successfully` : `AV Wallet credit processed successfully`,
          'success',
          successDetails
        );

      } else if (wallet === 'Main Wallet') {
        // AV Power Wallet - Save to wallets.working
        const currentWalletBalance = Number(userData.wallets?.working || 0);
        const newWalletBalance = operation === 'return'
          ? currentWalletBalance - amountNum
          : currentWalletBalance + amountNum;
        if (newWalletBalance < 0) {
          throw new Error(`Insufficient AV Power Wallet balance. Current: $${currentWalletBalance.toFixed(2)}`);
        }
        transactionData.newBalance = newWalletBalance;

        // Update wallets.working
        const walletUpdateData = {
          wallets: {
            ...userData.wallets,
            working: newWalletBalance
          }
        };

        await update(userRef, walletUpdateData);

        const successDetails = {
          memberName: userToUpdate.name,
          amount: amountNum,
          transactionType: operation === 'return' ? 'Debit AV Power' : 'Credit AV Power',
          newBalance: newWalletBalance,
          walletType: 'AV Power Wallet',
          sponsorId: sponsorId,
          transactionId: transactionId
        };

        await saveToAdminHistory(transactionData);
        await saveToUserFundTransactions(transactionData, userToUpdate, currentDate);

        showDialogMessage(
          '✅ Success', 
          operation === 'return' ? `AV Power Wallet debit processed successfully` : `AV Power Wallet credit processed successfully`,
          'success',
          successDetails
        );
      } else if (wallet === 'AV Pay Wallet') {
        const currentFundBalance = Number(userData.fund?.balance || 0);
        const newFundBalance = operation === 'return'
          ? currentFundBalance - amountNum
          : currentFundBalance + amountNum;
        if (newFundBalance < 0) {
          throw new Error(`Insufficient AV Pay Wallet balance. Current: $${currentFundBalance.toFixed(2)}`);
        }
        transactionData.newBalance = newFundBalance;

        const currentFundCount = Number(userData.fund?.count || 0);
        const currentFundTotal = Number(userData.fund?.total || 0);
        const signedAmount = operation === 'return' ? -amountNum : amountNum;

        const fundUpdateData = {
          fund: {
            ...(userData.fund || {}),
            balance: newFundBalance,
            count: currentFundCount + 1,
            lastAmount: signedAmount,
            lastDate: currentDate,
            lastTransactionId: transactionId,
            lastTransactionType: operation === 'return' ? 'Debit AV Pay Wallet' : 'Credit AV Pay Wallet',
            lastTransactionWallet: 'AV Pay Wallet',
            total: currentFundTotal + signedAmount
          }
        };

        await update(userRef, fundUpdateData);

        const successDetails = {
          memberName: userToUpdate.name,
          amount: amountNum,
          transactionType: operation === 'return' ? 'Debit AV Pay Wallet' : 'Credit AV Pay Wallet',
          newBalance: newFundBalance,
          walletType: 'AV Pay Wallet',
          sponsorId: sponsorId,
          transactionId: transactionId
        };

        await saveToAdminHistory(transactionData);
        await saveToUserFundTransactions(transactionData, userToUpdate, currentDate);

        showDialogMessage(
          '✅ Success',
          operation === 'return' ? `AV Pay Wallet debit processed successfully` : `AV Pay Wallet credit processed successfully`,
          'success',
          successDetails
        );
      }

      // Reset form
      handleClearUser();
      setAmount('');
      setWallet('');
      setOperation('add');
      setType('');
      setDescription('');
      
      // Refresh user data
      fetchUsers();

    } catch (error) {
      console.error('Error:', error);
      showDialogMessage('❌ Failed', `Error: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    handleClearUser();
    setAmount('');
    setWallet('');
    setOperation('add');
    setType('');
    setDescription('');
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  // Dialog Component
  const Dialog = () => {
    if (!showDialog) return null;

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div 
          className="bg-[#1A1F2E] rounded-xl max-w-md w-full border border-[#2A2F3E] shadow-2xl"
          style={{
            background: dialogType === 'success' 
              ? 'linear-gradient(135deg, #1A1F2E 0%, #1E2433 50%, #222838 100%)'
              : 'linear-gradient(135deg, #2A1F1F 0%, #332323 50%, #3D2727 100%)'
          }}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">{dialogTitle}</h3>
              <button onClick={closeDialog} className="text-gray-400 hover:text-white">✕</button>
            </div>
            
            <div className="text-white mb-6">
              <p className="mb-4 text-gray-300 text-sm">{dialogMessage}</p>
              
              {transactionDetails && dialogType === 'success' && (
                <div className="bg-black/20 rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-gray-400 text-xs">Member</p>
                      <p className="text-white font-medium text-sm">{transactionDetails.memberName}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Transaction ID</p>
                      <p className="text-blue-400 text-xs font-mono">{transactionDetails.transactionId}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-gray-400 text-xs">Sponsor ID</p>
                      <p className="text-blue-400 text-xs font-mono">{transactionDetails.sponsorId}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Amount</p>
                      <p className="text-green-400 font-bold text-base">${transactionDetails.amount.toFixed(2)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">New Balance</p>
                    <p className="text-yellow-400 font-bold text-base">${transactionDetails.newBalance.toFixed(2)}</p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end">
              <button 
                onClick={closeDialog} 
                className="px-5 py-1.5 bg-[#2A6BFF] hover:bg-[#1A5BEF] rounded-md text-white text-sm font-medium transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Mobile Accordion Section Component
  const MobileAccordionSection = ({ id, title, icon, children }) => (
    <div className="bg-[#1A1F2E] rounded-xl mb-3 border border-[#2A2F3E] overflow-hidden">
      <button
        onClick={() => toggleAccordion(id)}
        className="w-full p-4 flex justify-between items-center bg-[#1E2433]"
      >
        <div className="flex items-center gap-3">
          <span className="text-[#2A6BFF]">{icon}</span>
          <span className="text-white font-medium text-sm">{title}</span>
        </div>
        <svg 
          className={`w-5 h-5 text-gray-400 transition-transform ${activeAccordion === id ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {activeAccordion === id && (
        <div className="p-4 bg-[#1A1F2E] border-t border-[#2A2F3E]">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B0E14] font-body p-3">
      <Dialog />

      {/* Header */}
      <div className="bg-[#1A1F2E] rounded-xl p-4 mb-3 border border-[#2A2F3E]">
        <h1 className="text-lg font-bold text-white">Fund Management - Admin Add</h1>
        <p className="text-gray-400 text-xs mt-1">Add funds to member wallets with history tracking</p>
      </div>

      {/* Mobile View */}
      {isMobile ? (
        <>
          {/* Form Accordion */}
          <MobileAccordionSection id="form" title="Add Funds" icon="💰">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* User Search */}
              <div>
                <label className="block text-gray-300 text-xs mb-1.5 font-medium">Search Member *</label>
                <div className="relative">
                  <input
                    type="text"
                    value={userSearchInput}
                    onChange={(e) => setUserSearchInput(e.target.value)}
                    placeholder="Name, ID, sponsor ID..."
                    className="w-full px-3 py-2.5 bg-[#0B0E14] border border-[#2A2F3E] rounded-lg text-white placeholder:text-gray-500 text-sm focus:outline-none focus:border-[#2A6BFF] focus:ring-1 focus:ring-[#2A6BFF]/50"
                    required
                  />
                  {selectedUserId && (
                    <button type="button" onClick={handleClearUser} className="absolute right-3 top-2.5 text-gray-400 hover:text-white">
                      ✕
                    </button>
                  )}
                </div>

                {showSuggestions && suggestions.length > 0 && (
                  <div className="mt-1 bg-[#0B0E14] border border-[#2A2F3E] rounded-lg max-h-40 overflow-y-auto">
                    {suggestions.map((user) => (
                      <div
                        key={user.userId}
                        onClick={() => handleUserSelect(user)}
                        className="px-3 py-2.5 hover:bg-[#1E2433] cursor-pointer border-b border-[#2A2F3E] last:border-b-0"
                      >
                        <div className="text-white text-xs font-medium">{user.name}</div>
                        <div className="text-gray-400 text-[10px]">
                          ID: {user.userId} | Sponsor: {user.sponsorId}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected User */}
              {selectedUser && (
                <div className="p-3 bg-[#0B0E14] rounded-lg border border-[#2A2F3E]">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white text-sm font-medium">{selectedUser.name}</span>
                    <button type="button" onClick={handleClearUser} className="text-red-400 text-xs hover:text-red-300">
                      Change
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-gray-400 text-[10px]">Member ID</div>
                      <div className="text-white text-xs font-mono">{selectedUser.userId}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-[10px]">Sponsor ID</div>
                      <div className="text-blue-400 text-xs font-mono">{selectedUser.sponsorId}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-[10px]">AV Wallet</div>
                      <div className="text-yellow-400 text-sm font-bold">
                        ${(selectedUser.wallets?.aurum || 0).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-[10px]">AV Power</div>
                      <div className="text-blue-400 text-sm font-bold">
                        ${(selectedUser.wallets?.working || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Amount */}
              <div>
                <label className="block text-gray-300 text-xs mb-1.5 font-medium">Amount in USD ($) *</label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount (min: $1)"
                  min="0.01"
                  step="0.01"
                  className="w-full px-3 py-2.5 bg-[#0B0E14] border border-[#2A2F3E] rounded-lg text-white placeholder:text-gray-500 text-sm focus:outline-none focus:border-[#2A6BFF] focus:ring-1 focus:ring-[#2A6BFF]/50"
                  required
                />
              </div>

              {/* Wallet Type */}
              <div>
                <label className="block text-gray-300 text-xs mb-1.5 font-medium">Select Payment Currency *</label>
                <select
                  value={wallet}
                  onChange={(e) => {
                    setWallet(e.target.value);
                    setType(getTransactionTypeLabel(e.target.value, operation));
                  }}
                  className="w-full px-3 py-2.5 bg-[#0B0E14] border border-[#2A2F3E] rounded-lg text-white text-sm focus:outline-none focus:border-[#2A6BFF] focus:ring-1 focus:ring-[#2A6BFF]/50"
                  required
                >
                  <option value="" className="bg-[#0B0E14]">-- Select Payment Currency --</option>
                  <option value="Fund Wallet" className="bg-[#0B0E14]">AV Wallet</option>
                  <option value="Main Wallet" className="bg-[#0B0E14]">AV Power Wallet</option>
                  <option value="AV Pay Wallet" className="bg-[#0B0E14]">AV Pay Wallet</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-300 text-xs mb-1.5 font-medium">Action *</label>
                <select
                  value={operation}
                  onChange={(e) => {
                    const value = e.target.value;
                    setOperation(value);
                    setType(getTransactionTypeLabel(wallet, value));
                  }}
                  className="w-full px-3 py-2.5 bg-[#0B0E14] border border-[#2A2F3E] rounded-lg text-white text-sm focus:outline-none focus:border-[#2A6BFF] focus:ring-1 focus:ring-[#2A6BFF]/50"
                  required
                >
                  <option value="add" className="bg-[#0B0E14]">Credit to User Wallet</option>
                  <option value="return" className="bg-[#0B0E14]">Debit from User Wallet</option>
                </select>
                <p className="text-gray-500 text-[10px] mt-1">
                  Credit adds balance, Debit deducts balance. History stores amount and wallet type.
                </p>
              </div>

              {/* Transaction Type - Auto filled */}
              <div>
                <label className="block text-gray-300 text-xs mb-1.5 font-medium">Transaction Type</label>
                <input
                  type="text"
                  value={type}
                  readOnly
                  className="w-full px-3 py-2.5 bg-[#0B0E14]/50 border border-[#2A2F3E] rounded-lg text-gray-300 text-sm cursor-not-allowed"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-gray-300 text-xs mb-1.5 font-medium">
                  Description {operation === 'return' ? '(Debit Reason Required)' : '(Optional)'}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={operation === 'return' ? 'Enter reason for debit...' : 'Enter transaction description...'}
                  rows="2"
                  className="w-full px-3 py-2.5 bg-[#0B0E14] border border-[#2A2F3E] rounded-lg text-white placeholder:text-gray-500 text-sm resize-none focus:outline-none focus:border-[#2A6BFF] focus:ring-1 focus:ring-[#2A6BFF]/50"
                  required={operation === 'return'}
                />
              </div>

              {/* Current Balance Info */}
              {selectedUser && wallet === 'Fund Wallet' && (
                <div className="bg-[#0B0E14] rounded-lg p-3 border border-[#2A2F3E]">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-xs">Current AV Wallet Balance:</span>
                    <span className="text-yellow-400 font-bold text-base">${(selectedUser.wallets?.aurum || 0).toFixed(2)}</span>
                  </div>
                </div>
              )}

              {selectedUser && wallet === 'Main Wallet' && (
                <div className="bg-[#0B0E14] rounded-lg p-3 border border-[#2A2F3E]">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-xs">Current AV Power Balance:</span>
                    <span className="text-blue-400 font-bold text-base">${(selectedUser.wallets?.working || 0).toFixed(2)}</span>
                  </div>
                </div>
              )}

              {selectedUser && wallet === 'AV Pay Wallet' && (
                <div className="bg-[#0B0E14] rounded-lg p-3 border border-[#2A2F3E]">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-xs">Current AV Pay Wallet Balance:</span>
                    <span className="text-green-400 font-bold text-base">${(selectedUser.fund?.balance || 0).toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={loading || !selectedUserId}
                  className="flex-1 bg-[#2A6BFF] hover:bg-[#1A5BEF] text-white py-2.5 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    operation === 'return' ? 'Debit Funds' : 'Credit Funds'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="flex-1 px-3 py-2.5 bg-[#2A2F3E] hover:bg-[#3A3F4E] text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Reset Form
                </button>
              </div>
            </form>
          </MobileAccordionSection>

          {/* Stats Accordion */}
          <MobileAccordionSection id="stats" title="Quick Stats" icon="📊">
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-[#0B0E14] p-3 rounded-lg border border-[#2A2F3E]">
                <div className="text-gray-400 text-[10px]">Total Users</div>
                <div className="text-white text-lg font-bold">{users.length}</div>
              </div>
              <div className="bg-[#0B0E14] p-3 rounded-lg border border-[#2A2F3E]">
                <div className="text-gray-400 text-[10px]">Total AV Wallet</div>
                <div className="text-yellow-400 text-lg font-bold">
                  ${users.reduce((sum, u) => sum + (u.wallets?.aurum || 0), 0).toFixed(2)}
                </div>
              </div>
              <div className="bg-[#0B0E14] p-3 rounded-lg border border-[#2A2F3E]">
                <div className="text-gray-400 text-[10px]">Total AV Power</div>
                <div className="text-blue-400 text-lg font-bold">
                  ${users.reduce((sum, u) => sum + (u.wallets?.working || 0), 0).toFixed(2)}
                </div>
              </div>
            </div>
          </MobileAccordionSection>
        </>
      ) : (
        /* Desktop View */
        <>
          {/* Form Card */}
          <div className="bg-[#1A1F2E] rounded-xl p-5 mb-3 border border-[#2A2F3E]">
            <h2 className="text-white font-semibold text-base mb-4">Fund Transaction</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* User Search - Full Width */}
                <div className="col-span-2">
                  <label className="block text-gray-300 text-xs mb-1.5 font-medium">Search Member *</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={userSearchInput}
                      onChange={(e) => setUserSearchInput(e.target.value)}
                      placeholder="Search by name, user ID, sponsor ID..."
                      className="w-full px-3 py-2.5 bg-[#0B0E14] border border-[#2A2F3E] rounded-lg text-white placeholder:text-gray-500 text-sm focus:outline-none focus:border-[#2A6BFF] focus:ring-1 focus:ring-[#2A6BFF]/50"
                      required
                    />
                    {selectedUserId && (
                      <button type="button" onClick={handleClearUser} className="absolute right-3 top-2.5 text-gray-400 hover:text-white">
                        ✕
                      </button>
                    )}
                  </div>

                  {showSuggestions && suggestions.length > 0 && (
                    <div className="mt-1 bg-[#0B0E14] border border-[#2A2F3E] rounded-lg max-h-40 overflow-y-auto">
                      {suggestions.map((user) => (
                        <div
                          key={user.userId}
                          onClick={() => handleUserSelect(user)}
                          className="px-3 py-2.5 hover:bg-[#1E2433] cursor-pointer border-b border-[#2A2F3E] last:border-b-0"
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="text-white text-xs font-medium">{user.name}</div>
                              <div className="text-gray-400 text-[10px]">
                                ID: {user.userId} | Sponsor: {user.sponsorId}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-yellow-400 text-xs">AV: ${(user.wallets?.aurum || 0).toFixed(2)}</div>
                              <div className="text-blue-400 text-xs">Power: ${(user.wallets?.working || 0).toFixed(2)}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-gray-300 text-xs mb-1.5 font-medium">Amount in USD ($) *</label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter amount (min: $1)"
                    min="0.01"
                    step="0.01"
                    className="w-full px-3 py-2.5 bg-[#0B0E14] border border-[#2A2F3E] rounded-lg text-white placeholder:text-gray-500 text-sm focus:outline-none focus:border-[#2A6BFF] focus:ring-1 focus:ring-[#2A6BFF]/50"
                    required
                  />
                </div>

                {/* Wallet Selection */}
                <div>
                  <label className="block text-gray-300 text-xs mb-1.5 font-medium">Select Payment Currency *</label>
                  <select
                    value={wallet}
                    onChange={(e) => {
                      setWallet(e.target.value);
                      setType(getTransactionTypeLabel(e.target.value, operation));
                    }}
                    className="w-full px-3 py-2.5 bg-[#0B0E14] border border-[#2A2F3E] rounded-lg text-white text-sm focus:outline-none focus:border-[#2A6BFF] focus:ring-1 focus:ring-[#2A6BFF]/50"
                    required
                  >
                    <option value="" className="bg-[#0B0E14]">-- Select Payment Currency --</option>
                    <option value="Fund Wallet" className="bg-[#0B0E14]">AV Wallet</option>
                    <option value="Main Wallet" className="bg-[#0B0E14]">AV Power Wallet</option>
                    <option value="AV Pay Wallet" className="bg-[#0B0E14]">AV Pay Wallet</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-300 text-xs mb-1.5 font-medium">Action *</label>
                  <select
                    value={operation}
                    onChange={(e) => {
                      const value = e.target.value;
                      setOperation(value);
                      setType(getTransactionTypeLabel(wallet, value));
                    }}
                    className="w-full px-3 py-2.5 bg-[#0B0E14] border border-[#2A2F3E] rounded-lg text-white text-sm focus:outline-none focus:border-[#2A6BFF] focus:ring-1 focus:ring-[#2A6BFF]/50"
                    required
                  >
                    <option value="add" className="bg-[#0B0E14]">Credit to User Wallet</option>
                    <option value="return" className="bg-[#0B0E14]">Debit from User Wallet</option>
                  </select>
                  <p className="text-gray-500 text-[10px] mt-1">
                    Credit adds balance, Debit deducts balance. History stores amount and wallet type.
                  </p>
                </div>

                {/* Transaction Type - Read Only */}
                <div>
                  <label className="block text-gray-300 text-xs mb-1.5 font-medium">Transaction Type</label>
                  <input
                    type="text"
                    value={type}
                    readOnly
                    className="w-full px-3 py-2.5 bg-[#0B0E14]/50 border border-[#2A2F3E] rounded-lg text-gray-300 text-sm cursor-not-allowed"
                  />
                </div>

                {/* Description - Full Width */}
                <div className="col-span-2">
                  <label className="block text-gray-300 text-xs mb-1.5 font-medium">
                    Description {operation === 'return' ? '(Debit Reason Required)' : '(Optional)'}
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={operation === 'return' ? 'Enter reason for debit...' : 'Enter transaction description...'}
                    rows="2"
                    className="w-full px-3 py-2.5 bg-[#0B0E14] border border-[#2A2F3E] rounded-lg text-white placeholder:text-gray-500 text-sm resize-none focus:outline-none focus:border-[#2A6BFF] focus:ring-1 focus:ring-[#2A6BFF]/50"
                    required={operation === 'return'}
                  />
                </div>
              </div>

              {/* Selected User Details */}
              {selectedUser && (
                <div className="bg-[#0B0E14] rounded-lg p-4 border border-[#2A2F3E]">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-white text-sm font-medium">Selected Member Details</h3>
                    <button
                      type="button"
                      onClick={handleClearUser}
                      className="text-red-400 text-xs hover:text-red-300"
                    >
                      Change Member
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <div className="text-gray-400 text-[10px]">Name</div>
                      <div className="text-white text-sm font-medium">{selectedUser.name}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-[10px]">Member ID</div>
                      <div className="text-white text-sm font-mono">{selectedUser.userId}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-[10px]">Sponsor ID</div>
                      <div className="text-blue-400 text-sm font-mono">{selectedUser.sponsorId}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-[10px]">Status</div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        selectedUser.status === 'active' 
                          ? 'bg-green-900/30 text-green-400' 
                          : 'bg-red-900/30 text-red-400'
                      }`}>
                        {selectedUser.status || 'inactive'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="flex justify-between items-center p-2.5 bg-[#0B0E14] rounded-lg border border-[#2A2F3E]">
                      <span className="text-gray-400 text-xs">AV Wallet Balance:</span>
                      <span className="text-yellow-400 font-bold text-base">${(selectedUser.wallets?.aurum || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center p-2.5 bg-[#0B0E14] rounded-lg border border-[#2A2F3E]">
                      <span className="text-gray-400 text-xs">AV Power Wallet Balance:</span>
                      <span className="text-blue-400 font-bold text-base">${(selectedUser.wallets?.working || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex justify-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={loading || !selectedUserId}
                  className="px-8 py-2.5 bg-[#2A6BFF] hover:bg-[#1A5BEF] text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                      Processing...
                    </>
                  ) : (
                    operation === 'return' ? 'Debit Funds' : 'Credit Funds'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-8 py-2.5 bg-[#2A2F3E] hover:bg-[#3A3F4E] text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Reset Form
                </button>
              </div>
            </form>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-[#1A1F2E] p-4 rounded-lg border border-[#2A2F3E]">
              <div className="text-gray-400 text-xs">Total Users</div>
              <div className="text-white text-xl font-bold mt-1">{users.length}</div>
              <div className="text-gray-500 text-[10px] mt-1">Registered members</div>
            </div>
            <div className="bg-[#1A1F2E] p-4 rounded-lg border border-[#2A2F3E]">
              <div className="text-gray-400 text-xs">Total AV Wallet</div>
              <div className="text-yellow-400 text-xl font-bold mt-1">
                ${users.reduce((sum, u) => sum + (u.wallets?.aurum || 0), 0).toFixed(2)}
              </div>
              <div className="text-gray-500 text-[10px] mt-1">Across all users</div>
            </div>
            <div className="bg-[#1A1F2E] p-4 rounded-lg border border-[#2A2F3E]">
              <div className="text-gray-400 text-xs">Total AV Power</div>
              <div className="text-blue-400 text-xl font-bold mt-1">
                ${users.reduce((sum, u) => sum + (u.wallets?.working || 0), 0).toFixed(2)}
              </div>
              <div className="text-gray-500 text-[10px] mt-1">Power wallet balance</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FundAddByAdmin;